<!--
  AbstractDatepicker.svelte — Custom date/time picker component.

  Supports three modes: date-only, datetime, or time-only.
  Shows a calendar popup for date selection and hour/minute selectors
  for time.  Emits the selected value as both a Date object and an
  ISO string via two-way bindings.

  @prop {'date'|'datetime'|'time'} mode — Selection mode (default: 'datetime').
  @prop {string|null} stringValue — ISO string representation (two-way).
  @prop {Date|null} value — Date object representation (two-way).
  @prop {boolean} inline — Render calendar inline instead of as a popup.
  @prop {boolean} allowNull — Show a "Clear" button to set value to null.
  @prop {boolean} showRelativeDate — Display relative time badge (e.g. "2 days ago").
-->
<script lang="ts">
	import { run } from 'svelte/legacy';

	import { Badge, Button } from 'flowbite-svelte';
	import { t, t2 } from '../i18n';

	
	
	/** Internal tracker for the last string value to avoid recursive updates. */
	let lastStringValue: string | null = $state(null);
	
	
	
	
	interface Props {
		/** Selection mode: date-only, datetime, or time-only. */
		mode?: 'date' | 'datetime' | 'time';
		/** ISO string representation of the currently selected date/time (two-way binding). */
		stringValue?: string | null;
		/** Date object representation of the currently selected date/time (two-way binding). */
		value?: Date | null;
		/** When true, render the calendar inline instead of in a dropdown popup. */
		inline?: boolean;
		/** When true, show a "Clear" button that sets the value to null. */
		allowNull?: boolean;
		/** When true, show a relative-date badge (e.g. "3 days ago") next to the formatted value. */
		showRelativeDate?: boolean;
		/** Callback fired when the selected date/time changes. */
		onchange?: (_value: Date | null) => void;
	}

	let {
		mode = 'datetime',
		stringValue = $bindable(null),
		value = $bindable(null),
		inline = false,
		allowNull = true,
		showRelativeDate = true,
		onchange
	}: Props = $props();

	/** Whether the calendar popup is currently visible. */
	let showPopup = $state(false);
	/** The month currently displayed in the calendar grid. */
	let currentMonth = $state(value ? new Date(value) : new Date());

	/** Locale for formatting — undefined lets the browser decide. */
	const locale: string | undefined = undefined;

	/** Previous value used to detect actual changes in the reactive block. */
	let lastValue: Date | null = $state(null);

	/** Human-readable formatted string shown in the trigger button. */
	let formattedValue: string = $state(t2('None'));
	/** Short weekday names (Mon, Tue, …) derived from the locale. */
	let weekdays: string[] = $state([]);
	/** Day-of-week index (0–6) for the first day of `currentMonth`. */
	let startDay: number = $state(0);
	/** Number of days in the displayed month. */
	let daysInCurrentMonth: number = 0;
	/** Array of day numbers [1 … daysInCurrentMonth]. */
	let days: number[] = $state([]);
	/** Currently selected hour (0–23). */
	let selectedHour: number = $state(0);
	/** Currently selected minute (0–59). */
	let selectedMinute: number = $state(0);

	/**
	 * Format a Date as a human-friendly relative string (e.g. "2 days ago", "in 3 hours").
	 * @param date - The date to format relative to now.
	 * @returns A relative time string.
	 */
	function formatRelativeDate(date: Date): string {
		const now = new Date();
		const diffMs = date.getTime() - now.getTime();
		const absDiff = Math.abs(diffMs);
		const isPast = diffMs < 0;

		const minutes = Math.floor(absDiff / 60000);
		const hours = Math.floor(absDiff / 3600000);
		const days = Math.floor(absDiff / 86400000);

		/** Build the label for the given unit count and unit name. */
		const fnLabel = (count: number, unit: string) =>
			isPast ? `${count} ${unit}${count !== 1 ? 's' : ''} ago` : `in ${count} ${unit}${count !== 1 ? 's' : ''}`;

		/** Return immediately when the difference is under a minute. */
		if (minutes < 1) return t2('just now');
		/** Fall back to minute-level granularity for sub-hour deltas. */
		if (minutes < 60) return fnLabel(minutes, 'minute');
		/** Fall back to hour-level granularity for sub-day deltas. */
		if (hours < 24) return fnLabel(hours, 'hour');
		/** Fall back to day-level granularity for sub-month deltas. */
		if (days < 30) return fnLabel(days, 'day');
		/** Fall back to month-level granularity for sub-year deltas. */
		if (days < 365) return fnLabel(Math.floor(days / 30), 'month');
		return fnLabel(Math.floor(days / 365), 'year');
	}

	/** Toggle the popup open/closed. */
	const fnTogglePopup = () => {
		showPopup = !showPopup;
	};

	/** Navigate to the previous month. */
	const fnOnSelectPreviousMonth = (e: Event) => {
		e.stopPropagation();
		currentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
		fnUpdateCurrentMonth();
	};

	/** Jump to today's date (preserving the selected time). */
	const fnOnSelectCurrentMonth = (e: Event) => {
		e.stopPropagation();
		currentMonth = new Date();
		const newDate = new Date(); // eslint-disable-line svelte/prefer-svelte-reactivity -- local variable
		if (value) {
			newDate.setHours(value.getHours(), value.getMinutes());
		}
		value = newDate;
	};

	/** Navigate to the next month. */
	const fnOnSelectNextMonth = (e: Event) => {
		e.stopPropagation();
		currentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
		fnUpdateCurrentMonth();
	};

	/** Recalculate calendar grid values for the current month. */
	const fnUpdateCurrentMonth = () => {
		startDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0).getDay();
		daysInCurrentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
		days = Array.from({ length: daysInCurrentMonth }, (_, i) => i + 1);
	};

	/** Handle clicking a day in the calendar grid. */
	const fnOnSelectDate = (e: Event, day: number) => {
		e.stopPropagation();
		let newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day); // eslint-disable-line svelte/prefer-svelte-reactivity -- local variable
		/** Preserve the existing time when a date is already selected, otherwise use the current time. */
		if (value) {
			newDate.setHours(value.getHours(), value.getMinutes());
		} else {
			const now = new Date();
			newDate.setHours(now.getHours(), now.getMinutes());
		}
		value = newDate;
		stringValue = value.toISOString();
		lastStringValue = stringValue;
		showPopup = false;
		onchange?.(value);
	};

	/** Reference to the hour <select> element. */
	let hourControl: HTMLSelectElement = $state(undefined)!;
	/** Reference to the minute <select> element. */
	let minuteControl: HTMLSelectElement = $state(undefined)!;

	/** Handle hour/minute selector changes. */
	const fnOnUpdateTime = () => {
		if (!value) {
			value = new Date(); // eslint-disable-line svelte/prefer-svelte-reactivity -- local variable
		}
		value.setHours(hourControl.selectedIndex, minuteControl.selectedIndex);
		value = new Date(value); // Trigger reactivity
		onchange?.(value);
	};

	/** Reactive block: sync stringValue→value and format the display string. */
	run(() => {
		if (stringValue && stringValue.length > 0 && stringValue !== lastStringValue) {
			value = new Date(stringValue);
		}
		/** Recompute formatted display, weekdays, and calendar when the value actually changes. */
		if (lastValue !== value || weekdays.length === 0) {
			lastValue = value;
			formattedValue = value
				? value.toLocaleString(locale, {
						dateStyle: mode === 'date' || mode === 'datetime' ? 'short' : undefined,
						timeStyle: mode === 'time' || mode === 'datetime' ? 'short' : undefined
					})
				: t2('None');

			weekdays = Array.from({ length: 7 }, (_, i) =>
				new Date(2024, 0, i + 1).toLocaleString(locale, { weekday: 'short' })
			);

			fnUpdateCurrentMonth();

			selectedHour = value ? value.getHours() : 0;
			selectedMinute = value ? value.getMinutes() : 0;

			stringValue = value ? value.toISOString() : '';
			lastStringValue = stringValue;
		}
	});
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<div
	class="group/calendar relative w-full cursor-pointer rounded-lg border border-gray-300 px-4 py-2 hover:border-primary-500 dark:border-gray-600 {showPopup
		? 'ring ring-primary-500'
		: ''}"
	onclick={fnTogglePopup}
>
	<span class="flex items-center gap-2 group-hover/calendar:text-black group-hover/calendar:dark:text-white">
		<i class="fa-solid fa-calendar"></i>
		{formattedValue}

		{#if showRelativeDate && value && mode !== 'time'}
			<Badge color="gray">
				{formatRelativeDate(value)}
			</Badge>
		{/if}
	</span>

	{#if showPopup}
		<div
			onclick={(e) => e.stopPropagation()}
			class="{inline
				? 'relative'
				: 'absolute border'} left-0 top-full z-10 mt-2 min-w-[280px] cursor-auto rounded-lg border-gray-300 bg-white p-4 shadow-lg dark:border-gray-600 dark:bg-zinc-900"
		>
			<div class="flex flex-col gap-2">
				{#if mode !== 'time'}
					<div>
						<div class="mb-2 flex items-center justify-between">
							<Button size="xs" onclick={fnOnSelectPreviousMonth}>
								<i class="fa-solid fa-chevron-left"></i>
							</Button>
							<span
								onclick={fnOnSelectCurrentMonth}
								class="cursor-pointer font-medium hover:text-black hover:dark:text-white"
							>
								{currentMonth.toLocaleString(locale, { month: 'long', year: 'numeric' })}
							</span>
							<Button size="xs" onclick={fnOnSelectNextMonth}>
								<i class="fa-solid fa-chevron-right"></i>
							</Button>
						</div>
						<div class="grid grid-cols-7 gap-1 text-sm">
							{#each weekdays as day (day)}
								<div class="text-center font-medium">{day}</div>
							{/each}
							{#each Array(startDay) as _, i (i)}
								<div></div>
							{/each}
							{#each days as day (day)}
								{@const isSelected =
									value &&
									value.getDate() === day &&
									value.getMonth() === currentMonth.getMonth() &&
									value.getFullYear() === currentMonth.getFullYear()}
								{@const isToday =
									!isSelected &&
									(() => {
										const today = new Date();
										return (
											today.getDate() === day &&
											today.getMonth() === currentMonth.getMonth() &&
											today.getFullYear() === currentMonth.getFullYear()
										);
									})()}
								<button
									onclick={(e) => fnOnSelectDate(e, day)}
									class="rounded p-1 text-center hover:bg-gray-200 hover:dark:bg-zinc-700 {isSelected
										? 'bg-blue-500 text-white'
										: 'bg-gray-100 dark:bg-zinc-800'} {isToday ? 'border border-blue-500' : ''}"
								>
									{day}
								</button>
							{/each}
						</div>
					</div>
				{/if}

				<div class="mt-2 flex items-center gap-2">
					{#if mode !== 'date'}
						<span>
							<i class="fa-solid fa-clock mr-1"></i>
							{$t("Time")}
						</span>
						<select
							bind:this={hourControl}
							onchange={fnOnUpdateTime}
							class="cursor-pointer rounded border border-gray-300 bg-gray-100 p-1 text-black hover:bg-gray-200 dark:border-gray-600 dark:bg-zinc-800 dark:text-white hover:dark:bg-zinc-700"
						>
							{#each Array(24) as _, currentHour (currentHour)}
								<option selected={selectedHour == currentHour} value={currentHour}
									>{currentHour.toString().padStart(2, '0')}</option
								>
							{/each}
						</select>
						<span>:</span>
						<select
							bind:this={minuteControl}
							onchange={fnOnUpdateTime}
							class="cursor-pointer rounded border border-gray-300 bg-gray-100 p-1 text-black hover:bg-gray-200 dark:border-gray-600 dark:bg-zinc-800 dark:text-white hover:dark:bg-zinc-700"
						>
							{#each Array(60) as _, currentMinute (currentMinute)}
								<option selected={selectedMinute == currentMinute} value={currentMinute}
									>{currentMinute.toString().padStart(2, '0')}</option
								>
							{/each}
						</select>
					{/if}
					{#if allowNull}
						<div class="grow"></div>
						<Button
							size="xs"
							color="red"
							disabled={value == null}
							onclick={(e: MouseEvent) => {
								e.stopPropagation();
								value = null;
								stringValue = '';
								lastStringValue = stringValue;
								showPopup = false;
								onchange?.(null);
							}}
						>
							<i class="fa-solid fa-xmark"></i>
							{$t("Clear")}
						</Button>
					{/if}
				</div>
			</div>
		</div>
	{/if}
</div>
